import React, { useMemo } from 'react';
import PageLayout from '../../components/PageLayout';
import PageHero from '../../components/PageHero';
import Button from '../../components/Button';

const BREADCRUMBS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services/ifys-services' },
  { label: 'Social Enterprises' },
];

const UACK_VIDEO_ID = 'Ag5JEXIzugk';

const MEAL_RECIPIENTS = [
  'Schools',
  'Students and families who do not qualify for Centrelink and/or don\u2019t have the community, family or friendship networks to rely on when in need.',
  'We receive referrals from Social Workers, Psychologists, Dietitians, the Salvos, St Vinnies, Queensland Health and more. We supply Community Centres, PCYC, Flexi Schools, Palliative Care providers, elderly people with limited support and/or mobility, individuals and families affected by illness/accident or financial difficulties, carers, single parents, Department of Housing bedsits and units, Caravan Parks, and the list goes on.',
  'The Kawana Hub was established in response to Homelessness during Covid. It continues as a collaboration between the Department of Housing and IFYS. All of the catering is conducted on site, providing 3 meals a day, 7 days a week, 365 days a year to over 60 residents.',
];

const SocialEnterprisesPage = () => {
  const uackEmbedUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://www.ifys.com.au';
    return `https://www.youtube-nocookie.com/embed/${UACK_VIDEO_ID}?origin=${encodeURIComponent(origin)}&rel=0&modestbranding=1`;
  }, []);

  return (
    <PageLayout activeLink="services">
      <PageHero
        title="Social Enterprises"
        backgroundImage="/images/service-headers/social_enterprises%201.jpg"
        breadcrumbs={BREADCRUMBS}
        variant="split"
      />

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-ifys-blue mb-6">
          Social Enterprises
        </h2>
      </section>

      {/* UACK */}
      <section className="bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="mb-10 pb-10 border-b border-gray-200 text-center">
            <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">
              Event: Nourishing Connections Lunch
            </h3>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center">
              <a
                href="https://buy.stripe.com/3cIbJ3gJi5wyehb4ah18c01"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 w-full sm:w-auto rounded-full bg-ifys-blue px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all duration-200 ease-smooth hover:bg-ifys-dark-blue hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ifys-blue focus-visible:ring-offset-2"
              >
                <svg className="w-5 h-5 transition-transform duration-200 ease-smooth group-hover:-rotate-6" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 010 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 010-4V7a2 2 0 00-2-2H5z" />
                </svg>
                Get Tickets Here
              </a>
              <a
                href="https://buy.stripe.com/3cIbJ3gJi5wyehb4ah18c01"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center justify-center gap-2.5 w-full sm:w-auto rounded-full border-2 border-ifys-blue bg-white px-7 py-3.5 text-base font-semibold text-ifys-blue shadow-sm transition-all duration-200 ease-smooth hover:bg-ifys-blue hover:text-white hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ifys-blue focus-visible:ring-offset-2"
              >
                <svg className="w-5 h-5 transition-transform duration-200 ease-smooth group-hover:scale-110" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                </svg>
                Buy Raffle Tickets Here
              </a>
            </div>
          </div>

          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">
            Urban Angels Community Kitchen (UACK)
          </h3>
          <div className="space-y-4 text-gray-700 text-base sm:text-lg leading-relaxed">
            <p>
              UACK provides a platform for bringing the community together to create a ready stock of
              tasty, nutritious frozen meals for those who need support.
            </p>
            <p>
              While our Program provides a very practical, immediate form of support for those in
              crisis or need, it also performs a deeper function. The gift of a meal not only
              nourishes but also helps those in need to feel supported and not so alone. Feedback from
              recipients of our meals indicates that this small gesture of providing a meal has helped
              those in distress to move closer to inner healing. The compassion of our volunteers who
              help prepare and distribute the food is transmitted through it and helps more than
              perhaps we can readily appreciate.
            </p>
            <p>
              What we do promotes social connection, both for those who are receiving and for those
              who are giving; community building is taking place. The volunteers enjoy coming together
              with other community minded people to contribute in a meaningful way. New relationships
              are formed, new skills are learnt and individuals become more connected to their
              communities.
            </p>
            <p className="font-semibold text-ifys-blue">
              Volunteers between the ages of 17 and 85 years are welcome to join the UACK team.
            </p>
          </div>

          <div className="mt-8 rounded-xl overflow-hidden shadow-lg">
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={uackEmbedUrl}
                title="Urban Angels Community Kitchen"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                loading="lazy"
              />
            </div>
          </div>

          <div className="mt-10">
            <h4 className="text-xl font-bold text-ifys-blue mb-4">Who receives our meals?</h4>
            <p className="text-gray-700 text-base sm:text-lg leading-relaxed mb-6">
              Production currently exceeds 12,000 meals per month and our volunteers deliver most of
              these meals door to door throughout the Sunshine Coast.
            </p>
            <ul className="space-y-4">
              {MEAL_RECIPIENTS.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="flex-shrink-0 mt-1 w-6 h-6 rounded-full bg-ifys-blue flex items-center justify-center">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </span>
                  <span className="text-gray-700 text-sm sm:text-base leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 p-6 bg-white rounded-2xl border border-gray-200 shadow-sm">
            <p className="text-sm text-gray-700 mb-4 leading-relaxed">
              If you are interested in supporting our community, please email or call us: Email:{' '}
              <a href="mailto:urbanangels@ifys.com.au" className="text-ifys-blue font-semibold hover:underline">
                urbanangels@ifys.com.au
              </a>{' '}
              Mobile:{' '}
              <a href="tel:0457615803" className="text-ifys-blue font-semibold hover:underline">
                0457 615 803
              </a>
            </p>
            <Button
              variant="primary"
              size="md"
              href="https://donorbox.org/uack"
              target="_blank"
              rel="noopener noreferrer"
            >
              Donate Here
            </Button>
          </div>
        </div>
      </section>

      {/* Project Paradigm */}
      <section>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h3 className="text-2xl sm:text-3xl font-bold text-ifys-blue mb-6">Project Paradigm</h3>
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            Project Paradigm is a national strategy that works to support prevention, early
            intervention and community capacity building in the effort to ensure children and young
            people are free from harm through sexual exploitation.
          </p>
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed mt-4">
            For more information on Project Paradigm including resources and publications please visit
            the{' '}
            <a
              href="https://projectparadigm.com.au/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ifys-blue font-semibold hover:underline"
            >
              Project Paradigm website
            </a>.
          </p>
        </div>
      </section>
    </PageLayout>
  );
};

export default SocialEnterprisesPage;
